import React, { useEffect, useMemo, useState } from "react";
import { Download, RefreshCw } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import api from "../../api/api";
import Heading from "../../components/ui/Heading";
import { getSessionUsername } from "../../utils/sessionUser";

const today = () => new Date().toISOString().split("T")[0];

const firstDayOfMonth = () => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().split("T")[0];
};

const valueFrom = (row, keys, fallback = "") => {
  const key = keys.find((item) => row?.[item] !== undefined && row?.[item] !== null);
  return key ? row[key] : fallback;
};

const numberFrom = (row, keys) => Number(valueFrom(row, keys, 0) || 0);

const amountText = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const csvValue = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

const normalizeStatementRow = (row, index) => ({
  slno: valueFrom(row, ["Slno", "slno", "SlNo", "SLNO", "SNo"], index + 1),
  accountHead: valueFrom(row, ["AccountHead", "Accounthead", "CashBankLedger"]),
  refType: valueFrom(row, ["reftype", "RefType", "Ref Type"]),
  refNo: valueFrom(row, ["RefNo", "Refno", "Ref No"]),
  refDate: valueFrom(row, ["refdate", "RefDate", "Ref Date"]),
  account: valueFrom(row, ["Account", "Account1"]),
  particulars: valueFrom(row, ["Particulars", "particulars", "Narration"], ""),
  inward: numberFrom(row, ["inward", "Inward"]),
  outward: numberFrom(row, ["outward", "Outward"]),
  balance: numberFrom(row, ["balance", "Balance"]),
});

const normalizeOpeningRow = (row, index) => ({
  slno: valueFrom(row, ["Slno", "slno", "SlNo", "SLNO", "SNo"], index + 1),
  accountHead: valueFrom(row, ["AccountHead", "Accounthead", "CashBankLedger"]),
  inward: numberFrom(row, ["Inward", "inward"]),
  outward: numberFrom(row, ["Outward", "outward"]),
  balance: numberFrom(row, ["Balance", "balance"]),
});

const normalizeRefTypeRow = (row, index) => ({
  slno: valueFrom(row, ["Slno", "slno", "SlNo", "SLNO", "SNo"], index + 1),
  refType: valueFrom(row, ["reftype", "RefType", "Ref Type"]),
  accountHead: valueFrom(row, ["AccountHead", "Accounthead", "CashBankLedger"]),
  inward: numberFrom(row, ["Inward", "inward"]),
  outward: numberFrom(row, ["Outward", "outward"]),
});

const TabButton = ({ active, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-t-lg px-4 py-2 text-sm font-semibold transition ${
      active
        ? "bg-white text-teal-700 shadow-sm"
        : "text-slate-500 hover:text-slate-800"
    }`}
  >
    {children}
  </button>
);

const EmptyRow = ({ colSpan, text }) => (
  <tr>
    <td colSpan={colSpan} className="px-4 py-8 text-center text-slate-500">
      {text}
    </td>
  </tr>
);

const Bankstatement = () => {
  const userName = getSessionUsername();
  const [accountHeads, setAccountHeads] = useState([]);
  const [accountHeadId, setAccountHeadId] = useState("");
  const [fromDate, setFromDate] = useState(firstDayOfMonth);
  const [toDate, setToDate] = useState(today);
  const [statementRows, setStatementRows] = useState([]);
  const [openingRows, setOpeningRows] = useState([]);
  const [refTypeRows, setRefTypeRows] = useState([]);
  const [activeTab, setActiveTab] = useState("statement");
  const [generated, setGenerated] = useState(false);
  const [loadingHeads, setLoadingHeads] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchAccountHeads = async () => {
      setLoadingHeads(true);
      setError("");
      try {
        const res = await api.GetCashBankAccFL(51514, userName);
        const data = res?.GetCashBankAccFLjs || res;
        const heads = (data?.Table || [])
          .map((item) => ({
            id: item.AccId,
            name: item.CashBankLedger,
          }))
          .filter((item) => item.id !== undefined && item.name);

        setAccountHeads(heads);
        setAccountHeadId((current) => current || heads[0]?.id || "");
      } catch (err) {
        console.error("Failed to load account heads:", err);
        setAccountHeads([]);
        setAccountHeadId("");
        setError("Failed to load account heads.");
      } finally {
        setLoadingHeads(false);
      }
    };

    fetchAccountHeads();
  }, [userName]);

  const normalizedStatementRows = useMemo(
    () => statementRows.map((row, index) => normalizeStatementRow(row, index)),
    [statementRows]
  );

  const normalizedOpeningRows = useMemo(
    () => openingRows.map((row, index) => normalizeOpeningRow(row, index)),
    [openingRows]
  );

  const normalizedRefTypeRows = useMemo(
    () => refTypeRows.map((row, index) => normalizeRefTypeRow(row, index)),
    [refTypeRows]
  );

  const totals = useMemo(() => {
    const rows =
      activeTab === "opening"
        ? normalizedOpeningRows
        : activeTab === "reftype"
        ? normalizedRefTypeRows
        : normalizedStatementRows;

    return rows.reduce(
      (acc, row) => ({
        inward: acc.inward + Number(row.inward || 0),
        outward: acc.outward + Number(row.outward || 0),
        balance: row.balance !== undefined ? row.balance : acc.balance,
      }),
      { inward: 0, outward: 0, balance: 0 }
    );
  }, [activeTab, normalizedOpeningRows, normalizedRefTypeRows, normalizedStatementRows]);

  const getActiveExportData = (formatAmounts = false) => {
    const formatAmount = (value) => (formatAmounts ? amountText(value) : Number(value || 0));

    if (activeTab === "opening") {
      return {
        title: "Opening Summary",
        filenamePart: "opening-summary",
        headers: ["Sl No", "Account Head", "Inward", "Outward", "Balance"],
        rows: normalizedOpeningRows.map((row) => [
          row.slno,
          row.accountHead,
          formatAmount(row.inward),
          formatAmount(row.outward),
          formatAmount(row.balance),
        ]),
        footer: ["Total", "", formatAmount(totals.inward), formatAmount(totals.outward), formatAmount(totals.balance)],
      };
    }

    if (activeTab === "reftype") {
      return {
        title: "Reftype Summary",
        filenamePart: "reftype-summary",
        headers: ["Sl No", "Ref Type", "Account Head", "Inward", "Outward"],
        rows: normalizedRefTypeRows.map((row) => [
          row.slno,
          row.refType,
          row.accountHead,
          formatAmount(row.inward),
          formatAmount(row.outward),
        ]),
        footer: ["Total", "", "", formatAmount(totals.inward), formatAmount(totals.outward)],
      };
    }

    return {
      title: "Bankstatement Report",
      filenamePart: "statement",
      headers: [
        "Sl No",
        "Account Head",
        "Ref Type",
        "Ref No",
        "Ref Date",
        "Account",
        "Particulars",
        "Inward",
        "Outward",
        "Balance",
      ],
      rows: normalizedStatementRows.map((row) => [
        row.slno,
        row.accountHead,
        row.refType,
        row.refNo,
        row.refDate,
        row.account,
        row.particulars,
        formatAmount(row.inward),
        formatAmount(row.outward),
        formatAmount(row.balance),
      ]),
      footer: [
        "Total",
        "",
        "",
        "",
        "",
        "",
        "",
        formatAmount(totals.inward),
        formatAmount(totals.outward),
        formatAmount(totals.balance),
      ],
    };
  };

  const activeExportRowsCount =
    activeTab === "opening"
      ? normalizedOpeningRows.length
      : activeTab === "reftype"
      ? normalizedRefTypeRows.length
      : normalizedStatementRows.length;

  const selectedAccountHead =
    accountHeads.find((head) => String(head.id) === String(accountHeadId))?.name || "Account";

  const handleExportCsv = () => {
    const exportData = getActiveExportData(false);
    const csv = [exportData.headers, ...exportData.rows, exportData.footer]
      .map((line) => line.map(csvValue).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `bank-statement-${exportData.filenamePart}-${fromDate}-to-${toDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPdf = () => {
    const exportData = getActiveExportData(true);
    const doc = new jsPDF({ orientation: "landscape" });

    doc.setFontSize(14);
    doc.text(`Bank Statement - ${exportData.title}`, 14, 14);
    doc.setFontSize(9);
    doc.text(`${selectedAccountHead} | ${fromDate} to ${toDate}`, 14, 20);

    autoTable(doc, {
      startY: 25,
      head: [exportData.headers],
      body: exportData.rows,
      foot: [exportData.footer],
      theme: "grid",
      styles: { fontSize: activeTab === "statement" ? 7 : 8 },
      headStyles: { fillColor: [15, 23, 42] },
      footStyles: { fillColor: [15, 23, 42] },
    });

    doc.save(`bank-statement-${exportData.filenamePart}-${fromDate}-to-${toDate}.pdf`);
  };

  const handleGenerate = async () => {
    if (!accountHeadId) {
      setError("Please select account head.");
      return;
    }

    setLoading(true);
    setError("");
    setGenerated(true);
    setActiveTab("statement");
    try {
      const res = await api.CashBankStmtFL(accountHeadId, fromDate, toDate, "ABC123", userName);
      const data = res?.CashBankStmtFLjs || res;
      setStatementRows(data?.Table || []);
      setOpeningRows(data?.Table1 || []);
      setRefTypeRows(data?.Table2 || []);
    } catch (err) {
      console.error("Failed to load bank statement:", err);
      setStatementRows([]);
      setOpeningRows([]);
      setRefTypeRows([]);
      setError("Failed to load bank statement.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <Heading>Bank Statement</Heading>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-[1.4fr_1fr_1fr_auto] md:items-end">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">Account Head</label>
              <select
                value={accountHeadId}
                onChange={(event) => setAccountHeadId(event.target.value)}
                className="w-full rounded-lg border border-slate-200 bg-white p-3 text-slate-800 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              >
                <option value="">Select Account Head</option>
                {loadingHeads ? (
                  <option disabled>Loading...</option>
                ) : (
                  accountHeads.map((head) => (
                    <option key={head.id} value={head.id}>
                      {head.name}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                className="w-full rounded-lg border border-slate-200 p-3 text-slate-800 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
                className="w-full rounded-lg border border-slate-200 p-3 text-slate-800 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
              />
            </div>

            <button
              type="button"
              onClick={handleGenerate}
              disabled={loading || loadingHeads || !accountHeadId}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Generating" : "Generate"}
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-rose-100 bg-rose-50 p-4 font-semibold text-rose-700">
            {error}
          </div>
        )}

        {generated && (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 bg-slate-50 px-4 pt-3">
              <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={handleExportCsv}
                  disabled={loading || activeExportRowsCount === 0}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Download className="h-4 w-4" />
                  CSV
                </button>
                <button
                  type="button"
                  onClick={handleExportPdf}
                  disabled={loading || activeExportRowsCount === 0}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Download className="h-4 w-4" />
                  PDF
                </button>
              </div>
              <div className="flex flex-wrap gap-1">
                <TabButton active={activeTab === "statement"} onClick={() => setActiveTab("statement")}>
                  Bankstatement Report
                </TabButton>
                <TabButton active={activeTab === "opening"} onClick={() => setActiveTab("opening")}>
                  Opening Summary
                </TabButton>
                <TabButton active={activeTab === "reftype"} onClick={() => setActiveTab("reftype")}>
                  Reftype Summary
                </TabButton>
              </div>
            </div>

            {activeTab === "statement" && (
              <StatementTable rows={normalizedStatementRows} loading={loading} totals={totals} />
            )}
            {activeTab === "opening" && (
              <OpeningTable rows={normalizedOpeningRows} loading={loading} totals={totals} />
            )}
            {activeTab === "reftype" && (
              <RefTypeTable rows={normalizedRefTypeRows} loading={loading} totals={totals} />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StatementTable = ({ rows, loading, totals }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-slate-200 text-sm">
      <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
        <tr>
          <th className="px-4 py-3 text-left">Sl No</th>
          <th className="px-4 py-3 text-left">Account Head</th>
          <th className="px-4 py-3 text-left">Ref Type</th>
          <th className="px-4 py-3 text-left">Ref No</th>
          <th className="px-4 py-3 text-left">Ref Date</th>
          <th className="px-4 py-3 text-left">Account</th>
          <th className="px-4 py-3 text-left">Particulars</th>
          <th className="px-4 py-3 text-right">Inward</th>
          <th className="px-4 py-3 text-right">Outward</th>
          <th className="px-4 py-3 text-right">Balance</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 bg-white">
        {loading ? (
          <EmptyRow colSpan={10} text="Loading bank statement..." />
        ) : rows.length ? (
          rows.map((row) => (
            <tr key={`${row.slno}-${row.refNo}`} className="hover:bg-slate-50">
              <td className="whitespace-nowrap px-4 py-3 text-slate-600">{row.slno}</td>
              <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-800">{row.accountHead}</td>
              <td className="whitespace-nowrap px-4 py-3 text-slate-600">{row.refType}</td>
              <td className="whitespace-nowrap px-4 py-3 text-slate-600">{row.refNo}</td>
              <td className="whitespace-nowrap px-4 py-3 text-slate-600">{row.refDate}</td>
              <td className="whitespace-nowrap px-4 py-3 text-slate-600">{row.account}</td>
              <td className="px-4 py-3 text-slate-600">{row.particulars}</td>
              <td className="whitespace-nowrap px-4 py-3 text-right text-emerald-700">{amountText(row.inward)}</td>
              <td className="whitespace-nowrap px-4 py-3 text-right text-rose-700">{amountText(row.outward)}</td>
              <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-slate-800">{amountText(row.balance)}</td>
            </tr>
          ))
        ) : (
          <EmptyRow colSpan={10} text="No bank statement data found." />
        )}
      </tbody>
      {rows.length > 0 && !loading && (
        <tfoot className="bg-slate-50 text-sm font-bold text-slate-800">
          <tr>
            <td colSpan={7} className="px-4 py-3 text-right">Total</td>
            <td className="px-4 py-3 text-right">{amountText(totals.inward)}</td>
            <td className="px-4 py-3 text-right">{amountText(totals.outward)}</td>
            <td className="px-4 py-3 text-right">{amountText(totals.balance)}</td>
          </tr>
        </tfoot>
      )}
    </table>
  </div>
);

const OpeningTable = ({ rows, loading, totals }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-slate-200 text-sm">
      <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
        <tr>
          <th className="px-4 py-3 text-left">Sl No</th>
          <th className="px-4 py-3 text-left">Account Head</th>
          <th className="px-4 py-3 text-right">Inward</th>
          <th className="px-4 py-3 text-right">Outward</th>
          <th className="px-4 py-3 text-right">Balance</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 bg-white">
        {loading ? (
          <EmptyRow colSpan={5} text="Loading opening summary..." />
        ) : rows.length ? (
          rows.map((row) => (
            <tr key={`${row.slno}-${row.accountHead}`} className="hover:bg-slate-50">
              <td className="whitespace-nowrap px-4 py-3 text-slate-600">{row.slno}</td>
              <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-800">{row.accountHead}</td>
              <td className="whitespace-nowrap px-4 py-3 text-right text-emerald-700">{amountText(row.inward)}</td>
              <td className="whitespace-nowrap px-4 py-3 text-right text-rose-700">{amountText(row.outward)}</td>
              <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-slate-800">{amountText(row.balance)}</td>
            </tr>
          ))
        ) : (
          <EmptyRow colSpan={5} text="No opening summary data found." />
        )}
      </tbody>
      {rows.length > 0 && !loading && (
        <tfoot className="bg-slate-50 text-sm font-bold text-slate-800">
          <tr>
            <td colSpan={2} className="px-4 py-3 text-right">Total</td>
            <td className="px-4 py-3 text-right">{amountText(totals.inward)}</td>
            <td className="px-4 py-3 text-right">{amountText(totals.outward)}</td>
            <td className="px-4 py-3 text-right">{amountText(totals.balance)}</td>
          </tr>
        </tfoot>
      )}
    </table>
  </div>
);

const RefTypeTable = ({ rows, loading, totals }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-slate-200 text-sm">
      <thead className="bg-slate-100 text-xs uppercase tracking-wide text-slate-600">
        <tr>
          <th className="px-4 py-3 text-left">Sl No</th>
          <th className="px-4 py-3 text-left">Ref Type</th>
          <th className="px-4 py-3 text-left">Account Head</th>
          <th className="px-4 py-3 text-right">Inward</th>
          <th className="px-4 py-3 text-right">Outward</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 bg-white">
        {loading ? (
          <EmptyRow colSpan={5} text="Loading reftype summary..." />
        ) : rows.length ? (
          rows.map((row) => (
            <tr key={`${row.slno}-${row.refType}-${row.accountHead}`} className="hover:bg-slate-50">
              <td className="whitespace-nowrap px-4 py-3 text-slate-600">{row.slno}</td>
              <td className="whitespace-nowrap px-4 py-3 font-medium text-slate-800">{row.refType}</td>
              <td className="whitespace-nowrap px-4 py-3 text-slate-600">{row.accountHead}</td>
              <td className="whitespace-nowrap px-4 py-3 text-right text-emerald-700">{amountText(row.inward)}</td>
              <td className="whitespace-nowrap px-4 py-3 text-right text-rose-700">{amountText(row.outward)}</td>
            </tr>
          ))
        ) : (
          <EmptyRow colSpan={5} text="No reftype summary data found." />
        )}
      </tbody>
      {rows.length > 0 && !loading && (
        <tfoot className="bg-slate-50 text-sm font-bold text-slate-800">
          <tr>
            <td colSpan={3} className="px-4 py-3 text-right">Total</td>
            <td className="px-4 py-3 text-right">{amountText(totals.inward)}</td>
            <td className="px-4 py-3 text-right">{amountText(totals.outward)}</td>
          </tr>
        </tfoot>
      )}
    </table>
  </div>
);

export default Bankstatement;
